/**
 * MomentumHeatmap — visual readout of deal momentum tier distribution.
 *
 * Two views in one component:
 *  1. Horizontal stacked bar showing relative tier proportions
 *  2. Grid of squares (one per deal) coloured by tier — gives an
 *     at-a-glance "how many of each" without numbers.
 *
 * Pure SVG, no extra deps. Click a tile to navigate to that deal's
 * momentum detail page if the caller passed an `onDealClick`.
 */
import React from 'react';
import { MomentumTierCounts, HotDealEntry } from '../types/dealMomentum';

const TIER_COLOR: Record<string, string> = {
  hot:      '#dc2626', // red-600
  warming:  '#ea580c', // orange-600
  steady:   '#facc15', // yellow-400
  cooling:  '#0ea5e9', // sky-500
  cold:     '#6b7280', // gray-500
};

const TIER_LABEL: Record<string, string> = {
  hot:      'Hot',
  warming:  'Warming',
  steady:   'Steady',
  cooling:  'Cooling',
  cold:     'Cold',
};

interface Props {
  tierCounts: MomentumTierCounts;
  /** Optional list of individual deals (with their tier) to render as a
   * grid of squares. If omitted, only the stacked bar shows. */
  deals?: Array<HotDealEntry & { tier?: string }>;
}

const MomentumHeatmap: React.FC<Props> = ({ tierCounts, deals }) => {
  const total =
    (tierCounts.hot || 0) +
    (tierCounts.warming || 0) +
    (tierCounts.steady || 0) +
    (tierCounts.cooling || 0) +
    (tierCounts.cold || 0);

  if (total === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No deal momentum signals yet — they'll appear here as activity is recorded.
      </div>
    );
  }

  const tiers: Array<keyof MomentumTierCounts> = [
    'hot', 'warming', 'steady', 'cooling', 'cold',
  ];

  return (
    <div className="space-y-4">
      {/* Stacked bar */}
      <div>
        <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
          {tiers.map((t) => {
            const n = tierCounts[t] || 0;
            if (n === 0) return null;
            const pct = (n / total) * 100;
            return (
              <div
                key={t}
                style={{ width: `${pct}%`, backgroundColor: TIER_COLOR[t] }}
                title={`${TIER_LABEL[t]}: ${n} (${Math.round(pct)}%)`}
              />
            );
          })}
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-700">
          {tiers.map((t) => (
            <div key={t} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: TIER_COLOR[t] }}
              />
              <span className="font-medium">{TIER_LABEL[t]}</span>
              <span className="text-gray-500">{tierCounts[t] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-deal grid (heatmap) */}
      {deals && deals.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Each square = 1 deal · hover for name
          </div>
          <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(20px, 1fr))' }}>
            {deals.map((d) => {
              const tier = (d.tier as keyof MomentumTierCounts) || 'cold';
              const color = TIER_COLOR[tier] || TIER_COLOR.cold;
              const name = d.entity_name || d.startup?.name || d.project?.project_name || `#${d.id}`;
              return (
                <div
                  key={`heatcell-${d.id}`}
                  className="aspect-square rounded-sm transition-transform hover:scale-110 cursor-pointer"
                  style={{ backgroundColor: color }}
                  title={`${name} · ${TIER_LABEL[tier]}`}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MomentumHeatmap;
